import { addDays, format, setHours, setMinutes } from "date-fns";
import fr from "date-fns/locale/fr";
import React, { FunctionComponent, useState } from "react";
import DatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { z } from "zod";

import { usePost } from "../../../api-client/usePost";
import { useLanguage } from "../../../i18n/useLanguage";
import { useDealerAccountInfo } from "../../../stores/hooks/useDealerAccountInfo";
import { useDealerAddress } from "../../../stores/hooks/useDealerAddress";
import { useSetInfoToast } from "../../../stores/hooks/useSetInfoToast";
import { DealerHours } from "../../../stores/slices/dealerAccountInfoSlice";
import { PrimaryButton } from "../../../ui/Button/Button";
import { Input } from "../../../ui/Input/Input";
import { Select } from "../../../ui/Select/Select";
import { Textarea } from "../../../ui/Textarea/Textarea";
import { useForm } from "../../../util/useForm";
import { useOrderDetails } from "../useOrderDetails";

const schema = z.object({
  appointmentRequestDate: z
    .string()
    .nonempty({ message: "form.error.appointmentRequestDate" }),
  appointmentRequestTime: z
    .string()
    .nonempty({ message: "form.error.appointmentRequestTime" }),
  customerEmail: z.string().email({ message: "form.error.customerEmail" }),
  customerFirstName: z
    .string()
    .nonempty({ message: "form.error.customerFirstName" }),
  customerLastName: z
    .string()
    .nonempty({ message: "form.error.customerLastName" }),
  customerMessage: z.string().optional().default(""),
  customerPhone: z.string().nonempty({ message: "form.error.customerPhone" }),
});

export const ContactDealerForm: FunctionComponent = () => {
  const {
    dealerAppointmentLeadTime,
    dealerContactEmail,
    dealerContactPhone,
    dealerHours,
    dealerName,
  } = useDealerAccountInfo();
  const { t } = useTranslation();
  const { loading, send } = usePost(
    "/retailSiteWebService/shopping/lead/createQuoteAppointmentLead.php"
  );
  const [customerLanguage] = useLanguage();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const { data } = useOrderDetails();
  const [success, setSuccess] = useState<null | string>(null);
  const setInfoToast = useSetInfoToast();

  const [handleForm] = useForm({
    onSubmit({
      appointmentRequestTime,
      customerEmail,
      customerFirstName,
      customerLastName,
      customerMessage,
      customerPhone,
    }) {
      if (data) {
        send({
          appointmentRequestDate: format(selectedDate!, "yyyy-MM-dd"),
          appointmentRequestTime,
          customerContactMethodPreference: "0",
          customerEmail,
          customerFirstName,
          customerLanguage,
          customerLastName,
          customerMessage,
          customerPhone,
          orderNumber: data.orderNumber,
          sendCustomerEmail: "true",
          targetDealerId: data.dealerId,
        }).then(({ message }) => {
          setInfoToast({ message });
          setSuccess(message);
        });
      }
    },
    schema,
  });

  const timeOptions = getTimeOptions(
    selectedDate,
    dealerHours,
    dealerAppointmentLeadTime
  );

  const [customerFirstName, customerLastName] =
    data?.customerName.split(" ") || [];

  return (
    <form
      action=""
      className="border border-edge rounded-panel p-4 md:p-8 space-y-4 md:space-y-8"
      noValidate
      onSubmit={handleForm}
    >
      <header>
        <h2 className="text-xl md:text-2xl font-medium">{dealerName}</h2>
        <small>
          {useDealerAddress()}
          <br />
          {[dealerContactEmail, dealerContactPhone].filter(Boolean).join(", ")}
        </small>
      </header>
      {success ? (
        <>
          <p>{success}</p>
          <p>{t("cart.appointmentRequestDisclaimer")}</p>
          <PrimaryButton
            onClick={(): void => {
              setSuccess(null);
            }}
            aria-label="contactDealerSuccessButton"
          >
            {t("cart.ok")}
          </PrimaryButton>
        </>
      ) : (
        <>
          <fieldset className="grid gap-2">
            <label
              className="text-sm text-ink-2 font-thin"
              htmlFor="first-name"
            >
              {t("form.label.firstName")}
            </label>
            <Input
              aria-label="yourFirstName"
              autoComplete="given-name"
              defaultValue={customerFirstName}
              id="first-name"
              name="customerFirstName"
              placeholder={t("form.placeholder.firstName")}
            />
          </fieldset>
          <fieldset className="grid gap-2">
            <label className="text-sm text-ink-2 font-thin" htmlFor="last-name">
              {t("form.label.lastName")}
            </label>
            <Input
              aria-label="yourLastName"
              autoComplete="family-name"
              defaultValue={customerLastName}
              id="last-name"
              name="customerLastName"
              placeholder={t("form.placeholder.lastName")}
            />
          </fieldset>
          <fieldset className="grid gap-2">
            <label className="text-sm text-ink-2 font-thin" htmlFor="email">
              {t("form.label.email")}
            </label>
            <Input
              aria-label="yourEmail"
              autoComplete="email"
              defaultValue={data?.customerEmail}
              id="email"
              name="customerEmail"
              placeholder={t("form.placeholder.email")}
              type="email"
            />
          </fieldset>
          <fieldset className="grid gap-2">
            <label
              className="text-sm text-ink-2 font-thin"
              htmlFor="phone-number"
            >
              {t("form.label.phoneNumber")}
            </label>
            <Input
              aria-label="yourPhoneNumber"
              autoComplete="tel"
              defaultValue={data?.customerPhone}
              id="phone-number"
              inputMode="tel"
              name="customerPhone"
              placeholder={t("form.placeholder.phoneNumber")}
              type="tel"
            />
          </fieldset>
          <fieldset className="grid gap-2">
            <label
              className="text-sm text-ink-2 font-thin"
              htmlFor="appointment-date"
            >
              {t("form.label.appointmentRequestDate")}
            </label>
            <DatePicker
              filterDate={(date): boolean =>
                isDateAvailable(date, dealerHours, dealerAppointmentLeadTime)
              }
              autoComplete="none"
              className="w-full"
              customInput={<Input aria-label="selectedDate" />}
              id="appointment-date"
              locale={customerLanguage === "fr" ? fr : undefined}
              minDate={getMinDate(dealerAppointmentLeadTime)}
              name="appointmentRequestDate"
              onChange={setSelectedDate}
              selected={selectedDate}
            />
          </fieldset>
          <fieldset className="grid gap-2">
            <label
              className="text-sm text-ink-2 font-thin"
              htmlFor="appointment-time"
            >
              {t("form.label.appointmentRequestTime")}
            </label>
            <Select
              aria-label="appointmentTime"
              disabled={!timeOptions.length}
              id="appointment-time"
              name="appointmentRequestTime"
            >
              {timeOptions.map((option) => (
                <option
                  key={option}
                  value={option.split(".").pop()?.toUpperCase()}
                >
                  {t(option)}
                </option>
              ))}
            </Select>
          </fieldset>
          <fieldset className="grid gap-2">
            <label
              className="text-sm text-ink-2 font-thin"
              htmlFor="customer-message"
            >
              {t("form.label.note")}
            </label>
            <Textarea
              aria-label="yourNotes"
              autoComplete="none"
              id="customer-message"
              name="customerMessage"
              rows={3}
            />
          </fieldset>
          <PrimaryButton
            aria-label="contactDealerSubmitButton"
            busy={loading}
            className="flex w-full h-14"
            disabled={!data}
            type="submit"
          >
            {t("cart.requestAppointment")}
          </PrimaryButton>
        </>
      )}
    </form>
  );
};

type DayOfWeek = "FRI" | "MON" | "SAT" | "SUN" | "THU" | "TUE" | "WED";

const getDayOfWeek = (date: Date): DayOfWeek => {
  const day = format(date, "EEE").toUpperCase();
  return day as DayOfWeek;
};

const getMinDate = (dealerAppointmentLeadTime: unknown): Date =>
  addDays(new Date(), (Number(dealerAppointmentLeadTime) || 0) + 1);

const isDateAvailable = (
  date: Date | null,
  dealerHours: DealerHours,
  dealerAppointmentLeadTime: number
): boolean => {
  if (!date) {
    return false;
  }
  const day = getDayOfWeek(date);
  const hours = dealerHours[day] || {};
  const { close, open } = hours;
  const minDate = getMinDate(dealerAppointmentLeadTime);
  return Boolean(open && close && date >= minDate);
};

const formatTimeToAmPm = (timeString: string): null | string => {
  if (!timeString.match(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/)) {
    return null;
  }
  const [hours, minutes] = timeString.split(":").map(Number);
  const date = new Date();
  const withHours = setHours(date, hours);
  const withMinutes = setMinutes(withHours, minutes);
  return format(withMinutes, "a");
};

const getTimeOptions = (
  date: Date | null,
  dealerHours: DealerHours,
  dealerAppointmentLeadTime: number
): string[] => {
  if (!date || !isDateAvailable(date, dealerHours, dealerAppointmentLeadTime)) {
    return [];
  }
  const day = getDayOfWeek(date);
  const hours = dealerHours[day] || {};
  const open = formatTimeToAmPm(hours.open || "");
  const close = formatTimeToAmPm(hours.close || "");
  const map = {
    AM: "cart.timeOptions.am",
    PM: "cart.timeOptions.pm",
  };
  const set = new Set([
    map[(open as keyof typeof map) || ""],
    map[(close as keyof typeof map) || ""],
  ]);
  return Array.from(set).filter(Boolean) as string[];
};
